import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createAppThunk, type IRes } from "store/modules/common";
import Http from "commons/Http";
import { API_USER } from "config/api";
import type {
  IPartners,
  IResPartnersList,
  IResUserInfoData,
  IResUserList,
  IUserFetc,
  IUserInfoData,
  IUserList,
  IUserUpdateFetc,
} from "./UserVo";
import type { IPageInfo } from "pages/notice/store/NoticeVo";
import type { IRadioItem } from "components/ui/RadioGroup";
import { dataUrlToBlob } from "commons/utils";
import { rdxResetPhoneInfo } from "pages/signup/store/signupR";
import { Grade } from "store/modules/auth/authVo";
import { rdxSetCookie } from "commons/cookies/cookiesR";

const name = "userManage";

export interface IUserManage {
  list: IUserList[];
  searchList?: IUserList[];
  page_info?: IPageInfo;
  user?: IUserInfoData;
  authRadioData?: Array<IRadioItem | { [key: string]: string }>;
  partnerList?: IPartners[];
  userAuth?: IUserList;
  isLoading: boolean;
  checkPartners?: string[];
  tempPasswd?: string;
  isTempPasswd?: boolean;
  isNewWrite?: boolean;
}

/**
 * 유저 목록 리스트 조회
 */
export const apiGuserList = createAppThunk<IResUserList, IPageInfo>(
  `${name}/apiGuserList`,
  async (params, thunkApi) => {
    params.size = thunkApi.getState().userManage.page_info!.size;
    const res = await Http.get<IRes<IResUserList>>(`${API_USER}`, {
      params,
    });

    if (res.data.content) {
      thunkApi.dispatch(
        rdxSetUserPageInfo({
          page: res.data.content.data.page_info.page,
          size: 20,
          total_elements: res.data.content.data.page_info.total_elements,
          totalPage: Math.ceil(res.data.content.data.page_info.total_elements! / 20),
        }),
      );
      thunkApi.dispatch(rdxSetUserList(res.data.content.data.list));
    }
    return res.data;
  },
);

// 해당 유저 '정보' 조회
export const apiGuserInfo = createAppThunk<IResUserInfoData, number>(
  `${name}/apiGuserInfo`,
  async (params, thunkApi) => {
    const id = params ? params : thunkApi.getState().auth.id;

    const res = await Http.get<IRes<IResUserInfoData>>(`${API_USER}/info/${id}`);

    if (res.data.content) {
      thunkApi.dispatch(rdxSetUserInfoData(res.data.content.data));
    }
    return res.data;
  },
);

// 해당 유저 '권한' 조회 (사용자 [편집] 버튼 눌렀을 때)
export const apiGuserAuth = createAppThunk<{ data: IUserList }, number>(
  `${name}/apiGuserAuth`,
  async (params, thunkApi) => {
    const res = await Http.get<IRes<{ data: IUserList }>>(`${API_USER}/${params}`);

    if (res.data.content) {
      thunkApi.dispatch(rdxSetUserAuthData(res.data.content.data));
      thunkApi.dispatch(rdxSetCheckPartnerList(res.data.content.data.list.map(x => x.code)));
      thunkApi.dispatch(
        rdxSetUserAuthRadioData([
          { id: "ADMIN", text: "운영자" },
          { id: "USER", text: "부운영자" },
        ]),
      );
    }
    return res.data;
  },
);

// 파트너사 리스트 조회(CP, PP, PN 리스트 조회)
export const apiGPartnerList = createAppThunk<IResPartnersList, { type: string; filter?: boolean }>(
  `${name}/apiGPartnerList`,
  async (param, thunkApi) => {
    const filter = param.filter ? param.filter : false;
    const type = param.type.toLocaleLowerCase();
    // const apiUrl =
    //   type === "all"
    //     ? `codebook/provider`
    //     : type === "con"
    //     ? `codebook/consalad/channel/code`
    //     : `codebook/provider/${param.type}`;
    const res = await Http.get<IRes<IResPartnersList>>("codebook/provider", {
      params: { filter: filter },
    });
    if (res.data.content) {
      thunkApi.dispatch(rdxSetPartnerList(res.data.content.data.list as IPartners[]));
    }
    return res.data;
  },
);

// 임시비밀번호 발급
export const apiGpwdIssued = createAppThunk<{ data: string }>(`${name}/apiGpwdIssued`, async (_, thunkApi) => {
  const res = await Http.get<IRes<{ data: string }>>(`${API_USER}/password`);

  if (res.data.content) {
    thunkApi.dispatch(rdxSetTempPasswd(res.data.content.data));
  }

  return res.data;
});

// CP,PP,PN 계정에서 자기 정보 저장할 때
export const apiPuserInfoSave = createAppThunk<IResUserInfoData, IUserInfoData>(
  `${name}/apiPuserInfoSave`,
  async (_, thunkApi) => {
    const id = thunkApi.getState().auth.id;
    const auth = thunkApi.getState().auth;

    const form = new FormData();
    form.append("manager_name", thunkApi.getState().ui!.inputText!["manager_name"]!);
    thunkApi.getState().signup.phone.token && form.append("phone_token", thunkApi.getState().signup.phone.token!);

    if (auth.role !== Grade.USER) {
      form.append("business_license", thunkApi.getState().ui!.inputText!["business_license"]!);
      form.append("business_name", thunkApi.getState().ui!.inputText!["business_name"]!);
      form.append("ceo_name", thunkApi.getState().ui!.inputText!["ceo_name"]!);

      form.append("bank_name", thunkApi.getState().ui!.inputText!["bank_name"]!);
      form.append("bank_user_name", thunkApi.getState().ui!.inputText!["bank_user_name"]!);
      form.append("bank_license", thunkApi.getState().ui!.inputText!["bank_license"]!);

      if (
        thunkApi.getState().ui!.inputFile!["insert_bankbook"] &&
        thunkApi.getState().ui!.inputFile!["insert_bankbook"].imageData !== undefined
      ) {
        const file = thunkApi.getState().ui!.inputFile!["insert_bankbook"];
        form.append("passbook_copy_image", dataUrlToBlob(file.imageData!), file.filename);
      }

      if (
        thunkApi.getState().ui!.inputFile!["insert_business_copy"] &&
        thunkApi.getState().ui!.inputFile!["insert_business_copy"].imageData !== undefined
      ) {
        const file1 = thunkApi.getState().ui!.inputFile!["insert_business_copy"];
        form.append("business_copy_image", dataUrlToBlob(file1.imageData!), file1.filename);
      }
    }

    const res = await Http.post<IRes<IResUserInfoData>>(`${API_USER}/info/${id}`, form, {
      headers: { "content-type": "multipart/form-data" },
    });
    if (res.data.content) {
      const date = new Date();
      date.setTime(date.getTime() + 60 * 60 * 1000);

      thunkApi.dispatch(rdxSetUserInfoData(res.data.content.data));
      thunkApi.dispatch(rdxResetPhoneInfo());
      if (res.data.content.is_new_write === true) {
        thunkApi.dispatch(
          rdxSetCookie({
            name: "isNewWrite",
            value: String(res.data.content.is_new_write),
            expire: date.toISOString() as string,
          }),
        );
      }
    }

    return res.data;
  },
);

// 신규 유저 생성
export const apiPaddUser = createAppThunk<IUserList, IUserFetc>(`${name}/apiPaddUser`, async (param, thunkApi) => {
  param.email = thunkApi.getState().ui.inputText && thunkApi.getState().ui.inputText!.userEmail!;
  param.password = thunkApi.getState().ui.inputText && thunkApi.getState().ui.inputText!.userPwd!;
  param.type = thunkApi.getState().ui.radioBox && thunkApi.getState().ui.radioBox!.userType!;
  param.authoritie = thunkApi.getState().ui.radioBox && thunkApi.getState().ui.radioBox!.userAuth!;

  const res = await Http.post<IRes<IUserList>>(`${API_USER}`, param);
  return res.data;
});

// 유저 권한 수정 (유저 [편집])
export const apiPupdateUser = createAppThunk<IUserList, IUserUpdateFetc>(
  `${name}/apiPupdateUser`,
  async (param, thunkApi) => {
    const id = thunkApi.getState().userManage.userAuth?.id;
    if (thunkApi.getState().userManage.isTempPasswd) {
      param.password = thunkApi.getState().ui.inputText && thunkApi.getState().ui.inputText!.userPwd1!;
    }
    param.type = thunkApi.getState().ui.radioBox && thunkApi.getState().ui.radioBox!.userType!;
    param.authoritie = thunkApi.getState().ui.radioBox && thunkApi.getState().ui.radioBox!.userAuth!;

    const res = await Http.post<IRes<IUserList>>(`${API_USER}/update/${id}`, param);
    return res.data;
  },
);

//유저 삭제
export const apiPuserDelete = createAppThunk<IUserList, number>(`${name}/apiPuserDelete`, async param => {
  const res = await Http.post(`/${API_USER}/delete/${param}`);
  return res.data;
});

const userSlice = createSlice({
  name,
  initialState: {
    list: [],
    page_info: {
      total_elements: 0,
      page: 0,
      size: 20,
      totalPage: 0,
    },
    authRadioData: [
      { id: "MASTER", text: "마스터" },
      { id: "ADMIN", text: "운영자" },
      { id: "USER", text: "부운영자" },
    ],
    isLoading: false,
    isTempPasswd: false,
  } as IUserManage,
  reducers: {
    rdxSetUserPageInfo(state: IUserManage, action: PayloadAction<IPageInfo>) {
      state.page_info = action.payload;
    },
    rdxSetUserInfoData(state: IUserManage, action: PayloadAction<IUserInfoData>) {
      state.user = action.payload;
    },

    rdxSetPasswdFlag(state: IUserManage, action: PayloadAction<boolean>) {
      state.isTempPasswd = action.payload;
    },
    rdxSetCheckPartnerList(state: IUserManage, action: PayloadAction<string[]>) {
      state.checkPartners = action.payload;
    },
    rdxSetUserList(state: IUserManage, action: PayloadAction<IUserList[]>) {
      state.list = action.payload;
    },
    rdxSetSearchUserList(state: IUserManage, action: PayloadAction<IUserList[]>) {
      state.searchList = action.payload;
    },
    rdxResetSearchUserList(state: IUserManage) {
      delete state.searchList;
    },
    rdxSetTempPasswd(state: IUserManage, action: PayloadAction<string>) {
      state.tempPasswd = action.payload;
    },
    rdxSetUserAuthData(state: IUserManage, action: PayloadAction<IUserList>) {
      state.userAuth = action.payload;
    },
    rdxSetUserAuthRadioData(state: IUserManage, action: PayloadAction<Array<IRadioItem | { [key: string]: string }>>) {
      state.authRadioData = action.payload;
    },
    rdxSetPartnerList(state: IUserManage, action: PayloadAction<IPartners[]>) {
      state.partnerList = action.payload;
    },
    rdxResetUserInfoData(state: IUserManage) {
      delete state.user;
    },
    rdxResetPartnersList(state: IUserManage) {
      delete state.partnerList;
    },
    rdxResetPopupPage(state: IUserManage) {
      delete state.userAuth;
      delete state.checkPartners;
      delete state.tempPasswd;
      delete state.isTempPasswd;
      state.authRadioData = [
        { id: "MASTER", text: "마스터" },
        { id: "ADMIN", text: "운영자" },
        { id: "USER", text: "부운영자" },
      ];
    },
    rdxResetUserPage(state: IUserManage) {
      state.list = [];
      state.page_info = {
        page: 0,
        size: 20,
        total_elements: 0,
      };
      delete state.tempPasswd;
    },
  },
  extraReducers: builder => {
    builder.addCase(apiGuserInfo.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(apiGuserInfo.fulfilled, state => {
      state.isLoading = false;
    });
  },
});

export const {
  rdxSetUserPageInfo,
  rdxSetUserList,
  rdxResetUserPage,
  rdxSetUserInfoData,
  rdxSetTempPasswd,
  rdxResetUserInfoData,
  rdxSetPartnerList,
  rdxSetPasswdFlag,
  rdxSetUserAuthData,
  rdxResetPopupPage,
  rdxSetUserAuthRadioData,
  rdxResetPartnersList,
  rdxSetCheckPartnerList,
  rdxSetSearchUserList,
  rdxResetSearchUserList,
} = userSlice.actions;
// data 를 관리하는 reducer 기본 반환 설정
export default userSlice.reducer;
